import { countByStatus, isOverdue, KeyStatusCounts, sortKeysBySerial, typeObjectLinks } from '../../Helpers/keyRegisterUtils';
import { KeyType, RegisterKey } from '../../types/keyRegister';
import { EMPTY_SELECTION, KeySelection, matchesSelection } from './keySelection';

/**
 * Pure view logic for the nyckelregister page: which keys and types are in scope, the summary
 * card counts, the place and status-card filters, search and the per-type expand state.
 */

/** The status card's options that filter the table (`?status=`). `out` is every handed-out key, `overdue` the late ones among them. */
export type KeyStatusFilter = 'inStock' | 'out' | 'overdue' | 'lost' | 'blocked' | 'scrapped';
export const KEY_STATUS_FILTERS: KeyStatusFilter[] = ['inStock', 'out', 'overdue', 'lost', 'blocked', 'scrapped'];

export const parseStatusFilter = (value: string | null): KeyStatusFilter | null =>
    KEY_STATUS_FILTERS.find(filter => filter === value) ?? null;

/** The ⚙ toggles. Off: scrapped keys and keys of archived types are left out everywhere. */
export interface KeyScopeOptions {
    showScrapped: boolean;
    showArchived: boolean;
}

type PlaceFields = Pick<KeyType, 'opensBuildings' | 'opensUnits' | 'opensStorageUnits'>;

/** Whether the type is listed at all. */
export const isTypeInScope = (
    type: Pick<KeyType, 'archived'> & PlaceFields,
    options: KeyScopeOptions,
    selection: KeySelection = EMPTY_SELECTION
): boolean => (!type.archived || options.showArchived) && matchesSelection(type, selection);

/** Whether the key counts on the cards and can be a table row (its type must exist and be in scope). */
export const isKeyInScope = (
    key: Pick<RegisterKey, 'status' | 'typeKey'>,
    typesByKey: { [typeKey: string]: Pick<KeyType, 'archived'> & PlaceFields },
    options: KeyScopeOptions,
    selection: KeySelection = EMPTY_SELECTION
): boolean => {
    const type = typesByKey[key.typeKey];
    if (!type || !isTypeInScope(type, options, selection)) return false;
    return key.status !== 'scrapped' || options.showScrapped;
};

/**
 * Keys in scope per building: what "Alla nycklar" shows with only that building picked. A key whose
 * type opens several buildings counts in each of them.
 */
export const keyCountsByBuilding = (
    types: KeyType[],
    keysByType: { [typeKey: string]: RegisterKey[] },
    options: KeyScopeOptions
): { [buildingKey: string]: number } => {
    const counts: { [buildingKey: string]: number } = {};
    for (const type of types) {
        if (!isTypeInScope(type, options)) continue;
        const count = (keysByType[type.key] ?? []).filter(key => key.status !== 'scrapped' || options.showScrapped).length;
        const buildingKeys = new Set([...Object.keys(type.opensBuildings ?? {}), ...typeObjectLinks(type).map(link => link.buildingKey)]);
        buildingKeys.forEach(buildingKey => {
            counts[buildingKey] = (counts[buildingKey] ?? 0) + count;
        });
    }
    return counts;
};

export interface KeySummary {
    total: number;
    /** `counts.handedOut` is the Ute card: loans included. */
    counts: KeyStatusCounts;
    overdue: number;
}

export const summarizeKeys = (keys: Array<Pick<RegisterKey, 'status' | 'dueDate'>>, today: string): KeySummary => {
    const counts = countByStatus(keys);
    return {
        total: keys.length,
        counts,
        overdue: keys.filter(key => isOverdue(key, today)).length,
    };
};

/** Whole percent of `total`, 0 when there is nothing to divide. */
export const percentOf = (count: number, total: number): number => (total > 0 ? Math.round((count / total) * 100) : 0);

export const matchesStatusFilter = (
    key: Pick<RegisterKey, 'status' | 'dueDate'>,
    filter: KeyStatusFilter | null,
    today: string
): boolean => {
    switch (filter) {
        case null: return true;
        case 'inStock': return key.status === 'inStock';
        case 'out': return key.status === 'handedOut';
        case 'overdue': return isOverdue(key, today);
        case 'lost': return key.status === 'lost';
        case 'blocked': return key.status === 'blocked';
        case 'scrapped': return key.status === 'scrapped';
    }
};

export const normalizeQuery = (query: string): string => query.trim().toLocaleLowerCase('sv');

/** Case-insensitive substring match on serial, type name, holder name, förvaring and lock system. */
export const matchesSearch = (
    key: Pick<RegisterKey, 'serial' | 'location'>,
    type: Pick<KeyType, 'name' | 'lockSystemRef'>,
    holderName: string | undefined,
    normalizedQuery: string
): boolean => {
    if (!normalizedQuery) return true;
    return [key.serial, type.name, holderName, key.location, type.lockSystemRef]
        .some(field => !!field && field.toLocaleLowerCase('sv').includes(normalizedQuery));
};

export interface KeyTypeSection {
    type: KeyType;
    /** The type's keys in scope (sorted by serial): what the summary row counts. */
    scopeKeys: RegisterKey[];
    /** The keys listed as rows: `scopeKeys` narrowed by search and status filter. */
    rows: RegisterKey[];
    summary: KeySummary;
}

export interface BuildSectionsInput {
    types: KeyType[];
    keysByType: { [typeKey: string]: RegisterKey[] };
    options: KeyScopeOptions;
    /** Only the types matching the filter tree's picks. */
    selection?: KeySelection;
    query: string;
    statusFilter: KeyStatusFilter | null;
    today: string;
    holderNameOf: (key: RegisterKey) => string | undefined;
}

export const isFiltering = (query: string, statusFilter: KeyStatusFilter | null): boolean =>
    normalizeQuery(query) !== '' || statusFilter !== null;

/**
 * One section per type in scope, A–Ö. While searching or filtering on status, types without a
 * matching key are left out and each section lists only its matching keys.
 */
export const buildSections = ({ types, keysByType, options, selection = EMPTY_SELECTION, query, statusFilter, today, holderNameOf }: BuildSectionsInput): KeyTypeSection[] => {
    const normalized = normalizeQuery(query);
    const filtering = isFiltering(query, statusFilter);
    const sections: KeyTypeSection[] = [];
    const sorted = [...types].sort((a, b) => a.name.localeCompare(b.name, 'sv', { numeric: true }));
    for (const type of sorted) {
        if (!isTypeInScope(type, options, selection)) continue;
        const scopeKeys = sortKeysBySerial((keysByType[type.key] ?? []).filter(key => key.status !== 'scrapped' || options.showScrapped));
        const rows = filtering
            ? scopeKeys.filter(key => matchesStatusFilter(key, statusFilter, today) && matchesSearch(key, type, holderNameOf(key), normalized))
            : scopeKeys;
        if (filtering && rows.length === 0) continue;
        sections.push({ type, scopeKeys, rows, summary: summarizeKeys(scopeKeys, today) });
    }
    return sections;
};

/** Expanded/collapsed choices per type key. */
export type ExpandOverrides = { [typeKey: string]: boolean };

export interface ExpandStateInput {
    filtering: boolean;
    /** Number of sections shown. */
    visibleCount: number;
    /** Toggles made while not filtering; survive a search. */
    manual: ExpandOverrides;
    /** Toggles made while filtering; dropped when the filter is cleared. */
    whileFiltering: ExpandOverrides;
}

/**
 * Filtering: every section open unless closed during the filter. Otherwise the manual choice,
 * else the default: open only when it is the one section shown.
 */
export const isSectionExpanded = (typeKey: string, { filtering, visibleCount, manual, whileFiltering }: ExpandStateInput): boolean => {
    if (filtering) return whileFiltering[typeKey] ?? true;
    return manual[typeKey] ?? visibleCount === 1;
};
